import { AutogenLayoutCommissionsProcessingToolPanel } from "./autogen/AutogenLayoutCommissionsProcessingToolPanel";
import { Button, ChangeEvent, ClickEvent, Layout, Table, TableSelectionEvent, Toast } from "@mcleod/components";
import { CommissionsProcessingUtil } from "./CommissionsProcessingUtil";
import { Api, Logger, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { BulkUpdatesConfirmation } from "./BulkUpdatesConfirmation";
import { CommissionsProcessingTabLayout } from "./CommissionsProcessing";

const log: Logger = LogManager.getLogger("CommissionsProcessingToolPanel");

export class CommissionsProcessingToolPanel extends AutogenLayoutCommissionsProcessingToolPanel {
    private table: Table;

    override onLoad(): void {
        this.addMountListener(() => {
            if (CommissionsProcessingUtil.isCommissionsProcessingLayoutTab(this.owner)) {
                this.initialize(this.owner)
            }
        });
    }

    initialize(layout: CommissionsProcessingTabLayout) {
        this.table = layout.tableCommissionsProcessing;
        if (this.getAllVisibleButtons()?.length > 0) {
            this.table?.addSelectionListener((event: TableSelectionEvent) => {
                this.tableOnSelect(event);
            });
        } else {
            this.removeAll();
        }
    }

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    async tableOnSelect(event: TableSelectionEvent) {
        const modelRow = event.newRow?.data;
        await this.syncToolPanel(modelRow);
    }

    public async syncToolPanel(modelRow: ModelRow) {
        this.getAllVisibleButtons().forEach(button => {
            button.disabledTooltip = null;
            button.enabled = modelRow != null;
            if (button.enabled) {
                this.syncButtonState(button, modelRow);
            }
        });
    }

    private syncButtonState(button: Button, modelRow: ModelRow) {
        if (button === this.buttonUpdateStatuses) {
            // placeholder for when we need to disable the button due to some condition
        }
    }

    buttonUpdateStatusesOnClick(event: ClickEvent) {
        const selectedRows = this.table.selectedRows;
        const layout = Layout.getLayout("lme/settlement/commissions/BulkUpdatesConfirmation") as BulkUpdatesConfirmation;
        layout.addLayoutLoadListener(() => {
            if (selectedRows.every(row => row.data.get("ready_to_pay_flag") === "Y")) {
                layout.textboxStatus.selectedItem = layout.textboxStatus.items[1];
            } else {
                layout.textboxStatus.selectedItem = layout.textboxStatus.items[0];
            }
        });
        const recordCount = selectedRows.length;
        const confirmDialog = CommonDialogs.createDialog(layout, {
            title: `Update Status (${recordCount} ${recordCount > 1 ? "records" : "record"})`,
            okVisible: true
        });
        layout.addLayoutLoadListener(() => {
            if (this.table.selectedRows.every(row => row.data.get("ready_to_pay_flag") === "Y")) {
                layout.textboxStatus.selectedItem = layout.textboxStatus.items[1];
            } else {
                layout.textboxStatus.selectedItem = layout.textboxStatus.items[0];
            }
            layout.textboxStatus.addAfterDropdownSelectionListener(() => {
                layout.checkboxConfirmVoid.visible = layout.textboxStatus.valueAsString == "V";
                if (layout.checkboxConfirmVoid.visible) {
                    confirmDialog.setOkButtonProps({ enabled: false });
                } else {
                    confirmDialog.setOkButtonProps({ enabled: true });
                }

            });
            layout.checkboxConfirmVoid.addChangeListener((event: ChangeEvent) => {
                confirmDialog.setOkButtonProps({ enabled: event.newValue });
            });
        });

        confirmDialog.show().then(() => Promise.resolve(confirmDialog).then(async result => {
            if (!result.wasCancelled) {
                Api.post("lme/settlement/commissions/update-statuses", {
                    status: layout.textboxStatus.selectedItem.value,
                    commission_ids: selectedRows.map(row => row.data.get("id")).join(",")
                }).then((response) => {
                    const data = response?.data[0]
                    if (data == null) {
                        log.error("No data returned from update-statuses API call.");
                        return;
                    }
                    if (!StringUtil.isEmptyString(data?.error_reasons)) {
                        CommonDialogs.showError(data.error_reasons, `${data.error_count} record${data.error_count > 1 ? "s" : ""} failed to update.`);
                    } else {
                        Toast.showSuccessToast(`Updated ${data.num_records_processed} record${data.num_records_processed > 1 ? "s" : ""} to ${layout.textboxStatus.selectedItem.displayValue} status.`)
                    }
                    this.table.dataSource.search(this.table.dataSource.lastSearch);
                });
            }
        }));
    }
}
